import { Swap } from '@phosphor-icons/react';
import { EmployeeModel, EmployeePromotionModel } from 'api-hooks/employee';
import Button from 'components/elements/buttons/button';
import TableComponent, {
  IColumn,
} from 'components/elements/table-component/table-component';
import useDate from 'hooks/use-date';
import { TableContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

import useDeleteEmployeePromotionDialog from '../hooks/use-delete-employee-promotion-dialog';
import useDialogPromotionForm from '../hooks/use-dialog-promotion-form';

function useGetEmployeePromotionTableList(employeeId: string) {
  const { t } = useTranslation();
  const formatDate = useDate();
  const { onDeleteEmployeePromotion } =
    useDeleteEmployeePromotionDialog(employeeId);
  return React.useMemo<IColumn<EmployeePromotionModel>[]>(() => {
    return [
      {
        accessorKey: 'promotedAt',
        header: t('common:promoted_at'),
        cell({ row }) {
          const promotedAt = row.original.promotedAt;
          return promotedAt ? formatDate(promotedAt, 'dd MMM yyyy') : '-';
        },
      },
      {
        accessorKey: 'jobLevel',
        header: t('common:level'),
        cell({ row }) {
          const jobLevel = row.original.jobLevel;
          return t(`enum:${jobLevel}`);
        },
      },
      {
        accessorKey: 'id',
        header: t('common:action'),
        minSize: 200,
        cell({ row }) {
          const id = row.original.id;
          return (
            <Button
              buttonType="tertiary"
              startEnhancer={(color, size) => (
                <Swap size={size} color={color} />
              )}
              onClick={(e) => {
                e.stopPropagation();
                onDeleteEmployeePromotion(id)();
              }}
            >
              {t('common:revert_job_level')}
            </Button>
          );
        },
      },
    ];
  }, [formatDate, onDeleteEmployeePromotion, t]);
}

interface EmployeePromotionTableProps {
  employee: EmployeeModel;
}

export default function EmployeePromotionTable(
  props: EmployeePromotionTableProps,
) {
  const employeeId = props.employee.id;
  const columns = useGetEmployeePromotionTableList(employeeId);
  const promotions = props.employee.promotions;
  const { onClickEmployeePromotion } = useDialogPromotionForm(employeeId);

  return (
    <TableContainer>
      <TableComponent
        columns={columns}
        data={promotions}
        onRowClick={({ original }) => {
          onClickEmployeePromotion(original)();
        }}
      />
    </TableContainer>
  );
}
